/**
 * Getting ready for the line to drop, while it is up.
 *
 * Rendered inside the till while it is online. It keeps a copy of what the
 * till needs to sell offline — this site, its menu with prices, its tables,
 * which till this tablet is — and keeps this till holding receipt numbers:
 * a block of them is asked for once fewer than ten are left. Nothing shows
 * here; the line above the till (`gate.tsx`) is where any of this is said.
 */

import { useEffect, useRef } from 'react';
import {
  useEntitlements,
  useFloor,
  useMenu,
  useReserveOfflineBlock,
  type Location,
} from '@restaurant/api-client';

import { useThisTill } from '../this-till';
import { loadNumbers, numbersLeft, saveNumbers, saveSnapshot, withBlock } from './store';

/** Fewer than this many numbers in hand and another block is asked for. */
const TOP_UP_BELOW = 10;

export function OfflinePrep({ location }: { location: Location }) {
  const entitlements = useEntitlements();
  const entitled = entitlements.has('orders.offline');
  const menu = useMenu({ locationId: location.id, enabled: entitled });
  const floor = useFloor(entitled ? location.id : undefined);
  const till = useThisTill(location.id);
  const reserve = useReserveOfflineBlock();
  const terminalId = till.terminalId ?? null;

  // The snapshot, whenever what it is made of changes.
  useEffect(() => {
    if (!entitled || !menu.data) return;
    void saveSnapshot({
      savedAt: new Date().toISOString(),
      location: {
        id: location.id,
        name: location.name,
        currency: location.currency,
        locale: location.locale,
      },
      terminalId,
      entitled,
      menu: menu.data,
      tables: (floor.data ?? []).flatMap((plan) =>
        plan.tables.map((table) => ({ id: table.id, label: table.label })),
      ),
    });
  }, [entitled, menu.data, floor.data, terminalId, location]);

  // Receipt numbers in hand. Asked for at most once a minute: a refusal or a
  // lost answer must not turn into a request on every render.
  const { mutate } = reserve;
  const lastTry = useRef(0);
  useEffect(() => {
    if (!entitled || !terminalId) return;
    let cancelled = false;
    const topUp = async () => {
      if (Date.now() - lastTry.current < 60_000) return;
      const numbers = await loadNumbers();
      const mine = numbers?.terminalId === terminalId ? numbers : undefined;
      if (cancelled || numbersLeft(mine) >= TOP_UP_BELOW) return;
      lastTry.current = Date.now();
      mutate(
        { location_id: location.id, terminal_id: terminalId },
        {
          onSuccess: async (block) => {
            await saveNumbers(withBlock(await loadNumbers(), terminalId, block));
          },
        },
      );
    };
    void topUp();
    // Checked again every few minutes: numbers used up during an outage are
    // replaced once the line is back, without waiting for a reload.
    const timer = window.setInterval(() => void topUp(), 5 * 60_000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [entitled, terminalId, location.id, mutate]);

  return null;
}
